import { ref, watch } from 'vue';
import { route } from '@ht/lib-plural-vue';
import AnalyticsService from '@/js/analytics';
import useConfig from '@/composables/useConfig';
import useFaceDetection from '@/composables/useFaceDetection';
import { SYSTEM_VARIABLES_MAP } from '@/js/constants';
import useSequence from './useSequence';

const { getRobotToken } = route;
const lastAnalyticData = ref({});
const idleTimer = ref(null);
const sessionIdle = ref(false);

const analyticsHistory = ref([]);
const analyticsLoopTimer = ref(null);

const LOOP_TIME_WINDOW = 3;
const LOOP_MAX_COUNT = 5;
const LOOP_INTERVAL = 1000;

export default function useAnalytics() {
  const { currentConfig, sharedConfig, getVariable, isPreview, startNewSession } = useConfig();
  const { isFDActive } = useFaceDetection();
  const { interruptSequence } = useSequence();

  function init() {
    AnalyticsService.setGlobalProperties({
      // currentConfig specific properties
      projectId: currentConfig.value.projectId,
      projectName: currentConfig.value.projectName,
      organizationId: currentConfig.value.organisationId,
      organizationName: currentConfig.value.organisationName,
      robotId: currentConfig.value.robotId,
      robotName: currentConfig.value.robotName,
      robotApiKey: getRobotToken(),
      robotType: 'PLURAL-ONLINE',
      // environment
      environment: window.location.hostname,
      deviceId: sharedConfig.value.deviceId,
      // global meta
      isPreview: isPreview(),
      useSTT: currentConfig.value.speechServicesActive,
      useFaceDetection: currentConfig.value.recognitionServicesActive,
      language: getVariable(SYSTEM_VARIABLES_MAP.LANGUAGE_ISO2.id).value,
    });

    analyticsLoopTimer.value = setInterval(() => {
      if (analyticsHistory.value.length > 0) {
        const counts = analyticsHistory.value.reduce((acc, cur) => {
          if (!acc[cur.id]) {
            return {
              ...acc,
              [cur.id]: 1,
            };
          }
          return {
            ...acc,
            [cur.id]: acc[cur.id] + 1,
          };
        }, {});

        const loopTrigger = Object.entries(counts).find(([, count]) => count >= LOOP_MAX_COUNT);

        if (loopTrigger) {
          // Loop detected
          console.log('interrupting potential loop');
          console.log(`${loopTrigger[0]} was triggered ${loopTrigger[1]} times within the last ${LOOP_TIME_WINDOW} seconds`);
          interruptSequence();
          analyticsHistory.value = [];
        }

        // remove old entries
        analyticsHistory.value = analyticsHistory.value.filter((e) => e.ts + LOOP_TIME_WINDOW * 1000 > Date.now());
      }
    }, LOOP_INTERVAL);
  }

  function setGlobalProps(props) {
    AnalyticsService.setGlobalProperties(props);
  }

  watch(() => [getVariable(SYSTEM_VARIABLES_MAP.AGE.id), getVariable(SYSTEM_VARIABLES_MAP.GENDER.id)], (variables) => {
    AnalyticsService.setMetaProperties({
      age: variables[0]?.value || 0,
      gender: variables[1]?.value || '',
    });
  }, { deep: true, immediate: true });

  // the timer resets with each sequence change / action tracked (something happened)
  function startIdleTimer(analyticData) {
    // this optimisation is only used when the flow is not controlled by camera detection
    if (!isFDActive()) {
      if (sessionIdle.value) {
        startNewSession();
        analyticData.sessionId = getVariable(SYSTEM_VARIABLES_MAP.SESSION_ID.id).value;
        analyticData.sessionStartTime = getVariable(SYSTEM_VARIABLES_MAP.SESSION_START_TIME.id).value;
        analyticData.ts = getVariable(SYSTEM_VARIABLES_MAP.SESSION_START_TIME.id).value;
        sessionIdle.value = false;
      }
      if (idleTimer.value) {
        clearTimeout(idleTimer.value);
      }
      idleTimer.value = setTimeout(() => {
        sessionIdle.value = true;
      }, currentConfig.value.idleTimer);
    }
  }

  function trackAction(action) {
    startIdleTimer(action.analyticData);

    // we're filtering by title because canvas elements have a title and dynamically created ones (export) don't
    if (action?.params?.title) {
      AnalyticsService.trackElement(action.analyticData);
      lastAnalyticData.value = action.analyticData;
      analyticsHistory.value.push({
        id: action.analyticData.elementId,
        ts: Date.now(),
      });
    }
  }

  function amendAction(properties = {}) {
    if (lastAnalyticData.value) {
      startIdleTimer(lastAnalyticData.value);
      AnalyticsService.trackElement({
        ...lastAnalyticData.value,
        ...properties,
        amend: true,
      });
    }
  }

  return {
    init,
    trackAction,
    amendAction,
    setGlobalProps,
    lastAnalyticData,
  };
}
